import { Box, Button, Dialog, DialogActions, DialogContent, DialogContentText, Grid, IconButton, Typography } from "@mui/material";
import FullScreenModal from "../../../../components/common/modalPopup/FullScreenModal";
import {Document, Page, pdfjs } from "react-pdf";
import { ClientInvoiceAgent } from "../../../../app/api/clientInvoices/ClientInvoiceAgent";
import { useEffect, useState } from "react";
import getAxiosError from "../../../../app/api/axiosError";
import { toast } from "react-toastify";
import {
  RootState,
  useAppDispatch,
} from "../../../../app/features/store/store";
import Loader from "../../../../components/common/loader/Loader";
import { RatecardClientInvoiceResponse } from "../../../../app/models/responseModels/ClientInvoiceResponses";
import DownloadIcon from "@mui/icons-material/Download";
// these css removes extra text and space displayed at the end of the pdf without disabling text selection
import "react-pdf/dist/esm/Page/TextLayer.css";
import "react-pdf/dist/esm/Page/AnnotationLayer.css";
import { CLIENT_INVOICE_STATUS } from "../ClientInvoiceConstants";
import { useSelector } from "react-redux";
import InvoiceActionMenu from "./InvoiceActionMenu";
import ClientInvoiceComment from "./ClientInvoiceComment";
import dayjs from "dayjs";

  pdfjs.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL}/pdf.worker.js`;

  console.log("Global WorkSrc: ", pdfjs.GlobalWorkerOptions.workerSrc);
  
interface InvoicePreviewProps {
  invoice: RatecardClientInvoiceResponse | undefined;
  closeInvoicePreviewModal: CallableFunction;
  showInvoiceEditModal: CallableFunction;
}
const InvoicePreviewModal = (props: InvoicePreviewProps) => {
  const dispatch = useAppDispatch();
  const userSettings = useSelector(
    (state: RootState) => state.login.authResponse?.userSettings
  );

  const [numPages, setNumPages] = useState<number>();

  const [file, setFile] = useState<any>();
  const [invoiceFileResponse, setInvoiceFileResponse] = useState<any>();
  const [invoiceResponse, setInvoiceResponse] =
    useState<RatecardClientInvoiceResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [openNewCommentModal, setOpenNewCommentModal] =
    useState<boolean>(false);
  const [overrideDialogOpen, setOverrideDialogOpen] = useState<boolean>(false);
  const [invoiceExistsResponse, setInvoiceExistsResponse] = useState<boolean>(false);
  const [resetApproveSelection, setResetApproveSelection] = useState<boolean>(false);
  const handleSubmit = async () => {
    return true;
  };

  console.log("File is ", file);
  console.log("PDF JS Version: ", pdfjs.version);
  
  
  useEffect(() => {
    if (props.invoice) {
      setInvoiceResponse(props.invoice);
      fetchClientInvoiceFile(props.invoice.id, props.invoice.invoiceStatus);
      validateInvoice();
    }
  }, []);

  const validateInvoice = async () => {
    if (props.invoice) {
      try {
        const storeIds = props.invoice.stores.map((s) => s.storeId);
        const response = await ClientInvoiceAgent.validateInvoiceDetails(
          props.invoice.clientId,
          storeIds,
          dayjs(props.invoice.startDateRange).format("YYYY-MM-DD"),
          dayjs(props.invoice.endDateRange).format("YYYY-MM-DD")
        );
        setInvoiceExistsResponse(response.data === true);
      } catch {
        setInvoiceExistsResponse(false);
      }
    }
  };

  const fetchClientInvoiceFile = async (
    invoicePkId: number,
    invoiceStatus: string
  ) => {
    try {
      setLoading(true);
      const response = await ClientInvoiceAgent.fetchViewClientInvoiceFile(
        invoicePkId,
        invoiceStatus
      );
      setInvoiceFileResponse(response);
      setFile(new Blob([response.data]));
    } catch (error) {
      toast.error("File isn't accessible.", {
        toastId: "FetchClientInvoiceFileException",
      });
    } finally {
      setLoading(false);
    }
  }; 
  
  const executeApprove = async (submitType?: string) => {
    try {
      setLoading(true);
      if (props.invoice) {
        let actionStatus = "";
        if (
          userSettings?.roles.includes("FDC_OPS_Fin_admin") &&
          (props.invoice.invoiceStatus === CLIENT_INVOICE_STATUS.GENERATED ||
            props.invoice.invoiceStatus === CLIENT_INVOICE_STATUS.OPS_EDITED ||
            props.invoice.invoiceStatus === CLIENT_INVOICE_STATUS.OPS_REVIEW)
        ) {
          actionStatus = CLIENT_INVOICE_STATUS.OPS_APPROVED;
        }
        if (
          userSettings?.roles.includes("FDC_Fin_admin") &&
          (props.invoice.invoiceStatus === CLIENT_INVOICE_STATUS.OPS_APPROVED ||
            props.invoice.invoiceStatus === CLIENT_INVOICE_STATUS.FIN_EDITED)
        ) {
          actionStatus = CLIENT_INVOICE_STATUS.FIN_APPROVED;
        }
        const currentTimestamp = dayjs();
      
        const response = await ClientInvoiceAgent.changeInvoiceStatus(
          props.invoice.id,
          actionStatus,
          currentTimestamp,
          userSettings?.roles.includes("FDC_Fin_admin") ? submitType : undefined
        );
        
        if (response.isSuccess) {
          setInvoiceResponse(response.data);
          toast.success("Client invoice approved.", {
            toastId: "ClientInvoiceApprove",
          });
          props.closeInvoicePreviewModal();
        } else
          toast.error("Failed to approve client invoice.", {
            toastId: "ClientInvoiceApproveException",
          });
      }
    } catch (error) {
      const appError = getAxiosError(error);
      toast.error(
        `Failed to approve client invoice - ${appError.errorMessage}`,
        {
          toastId: "ClientInvoiceApproveException",
        }
      );
    } finally {
      setLoading(false);
    }
  };

  const handleApproveRadioClick = () => {
    if (props.invoice && props.invoice.currentStatus.internalStatus === "ops_approved" && invoiceExistsResponse) {
      setOverrideDialogOpen(true);
    }
  };

  const handleApprove = async (submitType?: string) => {
    executeApprove(submitType);
  };

  return (
    <FullScreenModal
      title="Invoice Preview"
      updateAsyncOperation={handleSubmit}
      closeFullScreenModal={props.closeInvoicePreviewModal}
      isLoading={loading}
      closeModalAfterSubmit={true}
      confirmMessage="Are you sure you want to delete?"
      isUpdatePermission={true}
      isDeletePermission={false}
      showSubmitButton={false}
      showDeleteButton={false}
    >
      <Box>
        {loading ? (
          <Loader isLoading={loading} />
        ) : (
          <>
            <Grid container justifyContent={"space-between"}>
              <Grid item>
                <IconButton
                  type="button"
                  sx={{ marginRight: 2 }}
                  onClick={() => {
                    const fileName = invoiceFileResponse.headers["file-name"];
                    const url = window.URL.createObjectURL(
                      new Blob([invoiceFileResponse.data])
                    );
                    const link = document.createElement("a");
                    link.href = url;
                    link.setAttribute("download", fileName);
                    document.body.appendChild(link);
                    link.click();
                  }}
                >
                  <DownloadIcon sx={{ color: "primary.main" }} />
                </IconButton>
                <Box sx={{ width: "fit-content" }}>
                  <Typography
                    p={0.5}
                    color={"white"}
                    fontWeight={"bold"}
                    sx={{ backgroundColor: "green" }}
                  >
                    Invoice Status:{" "}
                    {invoiceResponse?.currentStatus.externalStatus}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={8} sm={10} md={7} lg={7}>
                <ClientInvoiceComment
                  invoice={props.invoice}
                  newCommentModal={openNewCommentModal}
                  closeNewCommentModal={() => setOpenNewCommentModal(false)}
                />
              </Grid>
              <Grid item>
                <InvoiceActionMenu
                  invoice={props.invoice}
                  handleApprove={(submitType?: string) => handleApprove(submitType)}
                  handleApproveRadioClick={handleApproveRadioClick}
                  handleEdit={() => {
                    props.showInvoiceEditModal();
                  }}
                  handleFlag={() =>
                    setOpenNewCommentModal(!openNewCommentModal)
                  }
                  resetApproveSelection={resetApproveSelection}
                />
              </Grid>
            </Grid>
            <Box>
              <Document
                file={file}
                onLoadSuccess={({ numPages }: { numPages: number }) => {
                  setNumPages(numPages);
                }}
                onLoadError={(error) => console.error('Failed to load PDF: ', error)}
              >
                {Array.from(new Array(numPages), (_, index) => (
                  <>
                    <Page
                      key={`page_${index + 1}`}
                      pageNumber={index + 1}
                      width={Math.max(window.innerWidth * 0.95, 400)}
                      // checkout all answers from here - https://stackoverflow.com/questions/75104923/react-pdf-displaying-text-found-inside-the-pdf-instead-of-the-pdf-itself
                      // instead of this used css imports to fix these issues
                      //   renderTextLayer={false}
                      //   renderAnnotationLayer={false}
                    />
                    <p>
                      Page {index + 1} of {numPages}
                    </p>
                  </>
                ))}
              </Document>
            </Box>
          </>
        )}
      </Box>
      <Dialog open={overrideDialogOpen} onClose={() => { setOverrideDialogOpen(false); setResetApproveSelection(prev => !prev); }}>
        <DialogContent>
          <DialogContentText>
            An invoice already exists for the selected Stores and Date Range. Please delete the existing invoice before submitting a new one.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setOverrideDialogOpen(false); setResetApproveSelection(prev => !prev); }}>Ok</Button>
        </DialogActions>
      </Dialog>
    </FullScreenModal>
  );
};

export default InvoicePreviewModal;
